import { useCallback, useEffect, useState, useMemo } from 'react';
import useSWR from 'swr';
import { fromStatusCode } from 'pages/common/error/helpers';
import { addPage } from 'util/paginationUtils';
import { PaginatedResult, PaginationOptions } from 'types/api';
import { IResource } from '.';
import ApiBase from 'apis';
const api = ApiBase();

interface PaginatedResourceOptions<T> {
  mode?: 'replace' | 'append';
  deps?: any[]; // Dependencies that trigger data reset (filters, sort)
  uniqueSelector?: (item: T) => any; // For deduplication
  keepPreviousData?: boolean; // Keep previous data visible while fetching new data
}

export default function usePaginatedResource<T>(
  getUrl: (options: PaginationOptions) => string,
  initialPagination: PaginationOptions,
  onLoad?: (resource: PaginatedResult<T>) => void,
  options?: PaginatedResourceOptions<T>,
): readonly [
  IResource<PaginatedResult<T>>,
  () => void,
  () => Promise<void>,
  (updater: (current: PaginatedResult<T>) => PaginatedResult<T>) => void,
] {
  const [currentPage, setCurrentPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState<PaginatedResult<T> | null>(null);
  const [isSeeMoreLoading, setIsSeeMoreLoading] = useState(false);
  const limit = initialPagination.limit ?? 20;
  const mode = options?.mode ?? 'replace';

  // Generate cache key with dependencies for append mode to reset when filters change
  const key = useMemo(() => {
    const baseKey = getUrl({ page: 1, limit });
    // If the URL is empty/falsy, return null to prevent SWR from fetching
    if (!baseKey) return null;
    if (mode === 'append' && options?.deps) {
      const depsHash = JSON.stringify(options.deps);
      return `${baseKey}#${depsHash}`;
    }
    return baseKey;
  }, [getUrl, limit, mode, options?.deps]);

  const [isValidating, setIsValidating] = useState<boolean>();

  const {
    data,
    error,
    mutate,
    isValidating: swrIsValidating,
  } = useSWR<PaginatedResult<T>>(key, {
    revalidateOnFocus: false,
    keepPreviousData: options?.keepPreviousData,
    fetcher: async () => {
      if (mode === 'append') {
        // Check if we're beyond the first page (via closure access to accumulatedData)
        if (accumulatedData && accumulatedData.values.length > limit) {
          // We're on a later page - reload all accumulated data to preserve pagination
          const currentItemCount = accumulatedData.values.length;
          const targetPageSize = Math.min(currentItemCount, accumulatedData.total);
          const targetPage = Math.ceil(targetPageSize / limit);

          const url = getUrl({ page: 1, limit: targetPageSize });
          const result = await api.get<PaginatedResult<T>>(url);

          // Create a properly structured result that maintains original pagination structure
          const structuredResult = {
            ...result,
            limit: limit, // Keep original page limit
            page: targetPage, // Set the correct page number
          };

          setAccumulatedData(structuredResult);
          setCurrentPage(targetPage);
          return structuredResult;
        } else {
          // Initial load or only first page - fetch first page only
          const url = getUrl({ page: 1, limit });
          const result = await api.get<PaginatedResult<T>>(url);
          setAccumulatedData(result);
          setCurrentPage(1);
          return result;
        }
      } else {
        // For replace mode, fetch cumulative data (existing behavior)
        const url = getUrl({ page: 1, limit: limit * currentPage });
        const result = await api.get<PaginatedResult<T>>(url);
        return { ...result, limit, pageSize: result.pageSize - limit * (currentPage - 1) };
      }
    },
  });

  const state: IResource<PaginatedResult<T>> = error
    ? { state: 'error', errorType: fromStatusCode(error?.status) }
    : !data
      ? key === null
        ? { state: 'idle' } // No key means we're not fetching
        : { state: 'fetching' }
      : {
          state: 'fetched',
          resource:
            mode === 'append' && accumulatedData
              ? {
                  ...accumulatedData,
                  page: currentPage,
                }
              : {
                  ...data!,
                  page: currentPage,
                  values: mode === 'replace' ? data!.values.slice(0, limit * currentPage) : data!.values,
                },
          isValidating: isSeeMoreLoading ? false : (isValidating ?? swrIsValidating),
        };

  // Reset accumulated data when dependencies change (append mode only)
  useEffect(() => {
    if (mode === 'append' && !options?.keepPreviousData) {
      setAccumulatedData(null);
      setCurrentPage(1);
      setIsSeeMoreLoading(false);
    }
  }, [key, mode, options?.keepPreviousData]);

  useEffect(() => {
    if (state.state === 'fetched') onLoad?.(state.resource);
  }, [state.state, onLoad]);

  const onSeeMore = async () => {
    const newPage = currentPage + 1;
    if (state.state === 'fetched') {
      const data = state.resource;

      if (mode === 'append') {
        // For append mode, fetch the next individual page and merge
        setIsSeeMoreLoading(true);
        try {
          const url = getUrl({ page: newPage, limit });
          const result = await api.get<PaginatedResult<T>>(url);
          const newData = addPage(data, result, options?.uniqueSelector, true);
          setAccumulatedData(newData);
          setCurrentPage(newPage);
          mutate(newData, { revalidate: false });
        } catch (e) {
          // Handle error appropriately
          throw e;
        } finally {
          setIsSeeMoreLoading(false);
        }
      } else {
        // For replace mode, use existing behavior
        if (data.limit >= limit * newPage) {
          return;
        } else {
          const url = getUrl({ page: newPage, limit });
          setIsValidating(true);
          const result = await api.get<PaginatedResult<T>>(url);
          setCurrentPage(newPage);
          setIsValidating(false);
          const newData = addPage(data, result, undefined, false);
          mutate(newData, { revalidate: false });
        }
      }
    }
  };

  const reload = useCallback(() => {
    if (mode === 'append' && accumulatedData && accumulatedData.values.length > limit) {
      // For append mode, reload with enough items to match current accumulated data
      const currentItemCount = accumulatedData.values.length;
      const targetPageSize = Math.min(currentItemCount, accumulatedData.total);
      const targetPage = Math.ceil(targetPageSize / limit);

      // Don't reset state immediately to prevent pagination flash
      setIsSeeMoreLoading(false);

      // Trigger refetch with larger page size to maintain current view
      const expandedUrl = getUrl({ page: 1, limit: targetPageSize });
      mutate(
        async () => {
          const result = await api.get<PaginatedResult<T>>(expandedUrl);
          // Create a properly structured result that maintains original pagination structure
          const structuredResult = {
            ...result,
            limit: limit, // Keep original page limit
            page: targetPage, // Set the correct page number
          };
          setAccumulatedData(structuredResult);
          setCurrentPage(targetPage);
          return structuredResult;
        },
        { revalidate: false },
      );
    } else {
      // Normal reload for replace mode or when no accumulated data
      mutate();
    }
  }, [mutate, mode, accumulatedData, limit, getUrl]);

  // Optimistic update helper for append mode
  const optimisticUpdate = useCallback(
    (updater: (current: PaginatedResult<T>) => PaginatedResult<T>) => {
      if (mode === 'append' && accumulatedData) {
        const updated = updater(accumulatedData);
        setAccumulatedData(updated);
        mutate(updated, { revalidate: false });
      } else if (data) {
        const updated = updater(data);
        mutate(updated, { revalidate: false });
      }
    },
    [mode, accumulatedData, data, mutate],
  );

  return [state, reload, onSeeMore, optimisticUpdate] as const;
}
